import React, { useRef } from 'react';

/**
 * TavolaAnatomica — la tavola su cui si posano i punti, UNA per tutti.
 *
 * La usano le annotazioni dell'anamnesi (MappaAnatomica) e lo schema
 * «zone trattate» della procedura (MappaZone). Prima erano due
 * implementazioni: una giusta e una no, e la correzione sarebbe
 * arrivata a una sola. Qui sta tutto cio' che deve restare identico:
 *
 *  1. LE COORDINATE SONO PERCENTUALI DELL'IMMAGINE, non del riquadro
 *     che la contiene. Un riquadro ha padding, centratura, un'altezza
 *     massima che cambia con «Ingrandisci» e con la larghezza della
 *     finestra: un punto misurato sul riquadro si sposta rispetto al
 *     disegno ogni volta che il riquadro cambia. L'immagine no. Il
 *     clic si misura sul getBoundingClientRect() dell'<img>, e i punti
 *     stanno in un contenitore che ha ESATTAMENTE l'ingombro
 *     dell'immagine (inline-block, line-height 0).
 *
 *  2. UN PUNTO APPARTIENE A UNA TAVOLA, e si disegna SOLO sulla sua.
 *     Le otto tavole non sono allineate fra loro - il volto maschile e'
 *     alto due terzi di quello femminile, il corpo muscolare maschile e'
 *     largo tre quarti degli altri - quindi la chiave del punto e' la
 *     tavola intera: regione, sesso e strato. Un punto sulla fronte che
 *     ricompare sul corpo dice il falso su dove e' stato iniettato il
 *     prodotto, e lo dice in silenzio.
 *
 * Non salva niente e non sa cosa sia un punto oltre a x, y e mappa:
 * chi la usa passa i punti e dice come disegnarne uno (rendiPunto).
 */

export const AREE    = [['volto', 'Volto'], ['corpo', 'Corpo']];
export const SESSI   = [['f', 'Femminile'], ['m', 'Maschile']];
export const LIVELLI = [['superficiale', 'Superficiale'], ['muscolare', 'Muscolare']];

export const chiaveTavola = (area, sesso, livello) => `${area}_${sesso}_${livello}`;

export function scomponiTavola(mappa) {
    const [area, sesso, livello] = String(mappa || '').split('_');
    return { area, sesso, livello };
}

/* La tavola di partenza segue il sesso del paziente in anagrafica: un
   interruttore che il medico deve ricordarsi di girare e' un
   interruttore che qualche volta resta girato male. */
export const tavolaIniziale = (sessoPaziente) =>
    chiaveTavola('volto', sessoPaziente === 'M' ? 'm' : 'f', 'superficiale');

export function etichettaTavola(mappa) {
    const { area, sesso, livello } = scomponiTavola(mappa);
    const a = AREE.find((x) => x[0] === area)?.[1];
    const s = SESSI.find((x) => x[0] === sesso)?.[1];
    const l = LIVELLI.find((x) => x[0] === livello)?.[1];
    if (!a || !s || !l) return 'Tavola: da assegnare';
    return `${a} ${s.toLowerCase()} · ${l.toLowerCase()}`;
}

export const percorsoTavola = (mappa) => `/images/mappe/${mappa}.webp`;

/** Le coordinate del clic in percentuale dell'immagine, o null se
 *  fuori dall'immagine o se l'immagine non e' ancora misurabile. */
export function coordinateSuImmagine(evento, img) {
    if (!img) return null;
    const r = img.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    const x = ((evento.clientX - r.left) / r.width) * 100;
    const y = ((evento.clientY - r.top) / r.height) * 100;
    if (x < 0 || x > 100 || y < 0 || y > 100) return null;
    return { x: +x.toFixed(2), y: +y.toFixed(2) };
}

/** Solo i punti di QUESTA tavola. Un punto senza tavola non sta su nessuna. */
export const puntiDellaTavola = (punti, mappa) =>
    (punti || []).filter((p) => p.mappa && p.mappa === mappa);

/** I tre gruppi di pulsanti: regione, sesso, strato. */
export const SelettoreTavola = ({ mappa, onCambia, className = '' }) => {
    const { area, sesso, livello } = scomponiTavola(mappa);
    const gruppo = (voci, attuale, cambia, nome) => (
        <div className={`${className}gruppo`} role="group" aria-label={nome}>
            {voci.map(([v, e]) => (
                <button key={v} type="button" className={attuale === v ? 'attivo' : ''}
                        onClick={() => onCambia(cambia(v))}>{e}</button>
            ))}
        </div>
    );
    return (
        <>
            {gruppo(AREE, area, (v) => chiaveTavola(v, sesso, livello), 'Area')}
            {gruppo(SESSI, sesso, (v) => chiaveTavola(area, v, livello), 'Tavola')}
            {gruppo(LIVELLI, livello, (v) => chiaveTavola(area, sesso, v), 'Livello')}
        </>
    );
};

/**
 * @param mappa       la tavola in vista
 * @param punti       tutti i punti; si disegnano solo quelli con questa mappa
 * @param onClick     ({x, y}) => void, con le coordinate sull'immagine
 * @param rendiPunto  (punto, indiceNellElencoIntero) => elemento
 * @param children    altri elementi assoluti sull'immagine (il punto nuovo)
 */
export const TavolaAnatomica = ({
    mappa, punti = [], onClick, rendiPunto, children, className = '', imgClassName = '',
}) => {
    const rif = useRef(null);
    const { area, sesso, livello } = scomponiTavola(mappa);

    const clic = (e) => {
        if (!onClick) return;
        const c = coordinateSuImmagine(e, rif.current);
        if (c) onClick(c);
    };

    return (
        <div className={`ta-immagine ${className}`} onClick={clic}>
            <img ref={rif} src={percorsoTavola(mappa)} className={imgClassName}
                 alt={`Tavola anatomica ${area} ${sesso === 'm' ? 'maschile' : 'femminile'}, ${livello}`}
                 draggable="false" />
            {(punti || []).map((p, i) => (
                p.mappa === mappa ? (
                    <span key={p.chiave ?? p.id ?? i} className="ta-punto-ancora"
                          style={{ left: `${p.x}%`, top: `${p.y}%` }}>
                        {rendiPunto(p, i)}
                    </span>
                ) : null
            ))}
            {children}
        </div>
    );
};

export default TavolaAnatomica;
